/* eslint-disable react-hooks/exhaustive-deps */
import {
  InfoCircleOutlined,
  PlusOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Button,
  Empty,
  Image,
  message,
  Modal,
  Select,
  Table,
  Tooltip,
} from 'antd';
import {
  bulkAddParticipantsToCycle,
  removeParticipantsFromCycle,
  updateParticipantInCycle,
} from 'services/api/cycles';
import SelectParticipantsModal from './selectParticipantsModal';
import { useContext, useEffect, useState } from 'react';
import { CYCLE_STATUS, MODULES_ITEMS } from 'utils/constants';
import {
  bulkAddUsers,
  listSlackUsers,
  listUsers,
} from 'services/api/workspace';
import ProfileContext from 'services/profileContext';
import { useSearchParams } from 'react-router-dom';

const UserDisplayInSelect = ({ user }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <Avatar
      src={user.profile.profilePictureUrl}
      icon={<UserOutlined />}
      size={18}
      style={{ marginRight: 4, minWidth: 18, minHeight: 18 }}
    />
    {user.profile.firstName.length > 0 || user.profile.lastName.length > 0
      ? `${user.profile.firstName} ${user.profile.lastName}`
      : user.email}
    {user.slackUserId && (
      <Image
        src={`/img/slack_80.png`}
        style={{ width: 16, height: 16, marginLeft: 4 }}
        preview={false}
      />
    )}
  </div>
);

const CycleParticipants = ({ cycle, fetchCycle }) => {
  const [modalSelectParticipantsOpen, setModalSelectParticipantsOpen] =
    useState(false);
  const [users, setUsers] = useState([]);
  const { profile } = useContext(ProfileContext);
  const [searchParams] = useSearchParams();
  const [changePeerReviewLoading, setChangePeerReviewLoading] = useState(false);

  const addParticipants = (values) => {
    if (!values || values.length === 0) {
      setModalSelectParticipantsOpen(false);
      return;
    }
    const nonInvitedSlackUsers = values.filter((v) => v.slackUserId && !v.role);

    const functionToCall =
      nonInvitedSlackUsers && nonInvitedSlackUsers.length > 0
        ? bulkAddUsers({
            users: nonInvitedSlackUsers,
            sendInvitationEmail: false,
          })
        : Promise.resolve({});
    functionToCall
      .then((res) => {
        const addingParticipantsList = values.map((v) => ({
          userId:
            res.data?.find((e) => e.slackUserId === v.slackUserId)?._id ||
            v._id,
        }));
        bulkAddParticipantsToCycle({
          users: addingParticipantsList,
          cycleId: cycle._id,
        })
          .then((res) => {
            setModalSelectParticipantsOpen(false);
            fetchCycle();
          })
          .catch((e) => {
            message.error(e.message);
            console.error('Error adding participants to the cycle', e);
          });
      })
      .catch((e) => {
        console.error('Error adding slack users', e);
        if (e.response.data.error.code === 11000) {
          message.error(
            <div>
              <b>{e.response.data.error.keyValue.email}</b>
              &nbsp;already has an account on another workspace
            </div>
          );
        } else {
          message.error(e.message);
        }
      });
  };

  const removeParticipants = (participantId) => {
    removeParticipantsFromCycle({ userId: participantId, cycleId: cycle._id })
      .then((res) => {
        fetchCycle();
      })
      .catch((e) => {
        message.error(e.message);
        console.error('Error removing participants from the cycle', e);
      });
  };

  const updateParticipantDetails = (participantId, newParams) => {
    return updateParticipantInCycle({
      cycleId: cycle._id,
      userId: participantId,
      ...newParams,
    })
      .then(() => {
        fetchCycle();
        fetchUsers();
      })
      .catch((e) => {
        message.error(e.message);
        console.error('Error updating participant', e);
      });
  };

  const fetchUsers = () => {
    const shouldListSlack = profile.workspace.slack;
    const promises = [listUsers()];
    if (shouldListSlack) {
      promises.push(listSlackUsers());
    }
    Promise.all(promises)
      .then(([res, resSlack]) => {
        const slackUsersWithNoDuplicates = (resSlack?.data || []).filter(
          (e) =>
            !res.data.find(
              (e2) => e2.email.toLowerCase() === e.email.toLowerCase()
            )
        );
        const usersWithoutDuplicates = [
          ...(res.data || []),
          ...(slackUsersWithNoDuplicates || []),
        ];
        setUsers(usersWithoutDuplicates);
      })
      .catch((e) => {
        console.error('Error fetching users', e);
      });
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    const justConnectedSlack = searchParams.get('slackSuccess') === 'true';
    if (justConnectedSlack) {
      setModalSelectParticipantsOpen(true);
    }
  }, [searchParams]);

  return (
    <div>
      <div>
        <div style={{ color: '#878787' }}>
          Add reviewees to the cycle and define their roles, managers and peers
          if needed.
        </div>
      </div>
      {cycle.participants.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            disabled={cycle?.isArchived}
            type="primary"
            onClick={() => {
              setModalSelectParticipantsOpen(true);
            }}
            icon={<PlusOutlined />}
          >
            Add Participants
          </Button>
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <Table
          scroll={{ x: 400 }}
          pagination={false}
          bordered={false}
          size="small"
          locale={{
            emptyText: (
              <Empty
                description={<div>No participants added yet</div>}
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button
                  type="primary"
                  onClick={() => {
                    setModalSelectParticipantsOpen(true);
                  }}
                  icon={<PlusOutlined />}
                >
                  Add Participants
                </Button>
              </Empty>
            ),
          }}
          rowKey={(record) => `${record.email}_${record._id}`}
          columns={[
            {
              title: 'Name',
              dataIndex: 'firstName',
              key: 'name',
              render: (text, record) => {
                return (
                  <div>
                    <Avatar
                      size={26}
                      src={record.profile.profilePictureUrl}
                      icon={<UserOutlined />}
                    />
                    &nbsp;&nbsp;
                    {record.profile.firstName.length > 0 ||
                    record.profile.lastName.length > 0
                      ? `${record.profile.firstName} ${record.profile.lastName}`
                      : record.email}
                  </div>
                );
              },
            },
            ...(cycle.modules.find(
              (m) => m.type === MODULES_ITEMS.DOWNWARD.type
            )
              ? [
                  {
                    title: 'Manager Review',
                    dataIndex: 'managerReview',
                    key: 'managerReview',
                    render: (text, record) => {
                      return (
                        <Select
                          disabled={cycle?.isArchived}
                          prefix={<UserOutlined />}
                          placeholder="Reviewee's manager"
                          style={{
                            width: '100%',
                            minWidth: 180,
                            maxWidth: 320,
                          }}
                          popupMatchSelectWidth={false}
                          value={record.manager?._id}
                          allowClear
                          showSearch
                          filterOption={(val, opt) => {
                            const name =
                              `${opt.label.props.user.profile.firstName}` +
                              `${opt.label.props.user.profile.lastName}`;
                            return name
                              .toLowerCase()
                              .includes(val.toLowerCase());
                          }}
                          onChange={(value) => {
                            // if participant not invited, invite them
                            const userValue = users.find(
                              (u) => u._id === value
                            );
                            const functocall =
                              !userValue || userValue.role
                                ? Promise.resolve()
                                : bulkAddUsers({
                                    users: [userValue],
                                    sendInvitationEmail: false,
                                  });
                            functocall.then((res) => {
                              updateParticipantDetails(record._id, {
                                managerId: res?.data
                                  ? res.data[0]._id
                                  : value || null,
                              });
                            });
                          }}
                          options={users.map((u) => ({
                            label: <UserDisplayInSelect user={u} />,
                            value: u._id,
                          }))}
                        />
                      );
                    },
                  },
                ]
              : []),
            ...(cycle.modules.find((m) => m.type === MODULES_ITEMS.PEER.type)
              ? [
                  {
                    title: (
                      <Tooltip title="You nominate the peers, more nominate options coming soon">
                        Peer Review <InfoCircleOutlined />
                      </Tooltip>
                    ),
                    dataIndex: 'peerReview',
                    key: 'peerReview',
                    render: (text, record) => {
                      return (
                        <Select
                          disabled={cycle?.isArchived}
                          mode="multiple"
                          prefix={<UserOutlined />}
                          placeholder="Reviewee's peers"
                          style={{
                            width: '100%',
                            minWidth: 180,
                            maxWidth: 320,
                          }}
                          popupMatchSelectWidth={false}
                          filterOption={(val, opt) => {
                            const name =
                              `${opt.label.props.user.profile.firstName}` +
                              `${opt.label.props.user.profile.lastName}`;
                            return name
                              .toLowerCase()
                              .includes(val.toLowerCase());
                          }}
                          value={record.peers.map((e) => e._id)}
                          onChange={(value) => {
                            if (changePeerReviewLoading) {
                              return;
                            }
                            setChangePeerReviewLoading(true);
                            const enrichedValue = value.map((e) => {
                              const user = users.find((u) => u._id === e);
                              return {
                                userId: e,
                                ...(user || {}),
                              };
                            });
                            const slackUsersNotInvited = enrichedValue.filter(
                              (e) => e.slackUserId && !e.role
                            );
                            const funcToCall =
                              slackUsersNotInvited &&
                              slackUsersNotInvited.length > 0
                                ? bulkAddUsers({
                                    users: slackUsersNotInvited,
                                    sendInvitationEmail: false,
                                  })
                                : Promise.resolve();
                            funcToCall
                              .then((res) => {
                                const enrichedValueFiltered =
                                  enrichedValue.filter(
                                    (e) =>
                                      !slackUsersNotInvited.find(
                                        (e2) => e2.slackUserId === e.slackUserId
                                      )
                                  );
                                updateParticipantDetails(record._id, {
                                  peers: [
                                    ...enrichedValueFiltered,
                                    ...(res?.data ? res?.data : []),
                                  ].map((e) => ({
                                    userId: e._id,
                                  })),
                                }).finally(() => {
                                  setChangePeerReviewLoading(false);
                                });
                              })
                              .catch((r) => {
                                console.error(r);
                                setChangePeerReviewLoading(false);
                              });
                          }}
                          options={users.map((u) => ({
                            label: <UserDisplayInSelect user={u} />,
                            value: u._id,
                          }))}
                        />
                      );
                    },
                  },
                ]
              : []),
            ...(cycle.modules.find((m) => m.type === MODULES_ITEMS.UPWARD.type)
              ? [
                  {
                    title: 'Upward Review',
                    dataIndex: 'upwardReview',
                    key: 'upwardReview',
                    render: (text, record) => {
                      return (
                        <Select
                          disabled={cycle?.isArchived}
                          mode="multiple"
                          prefix={<UserOutlined />}
                          placeholder="Reviewee's reports"
                          style={{
                            width: '100%',
                            minWidth: 180,
                            maxWidth: 320,
                          }}
                          popupMatchSelectWidth={false}
                          value={record.reports.map((e) => e._id)}
                          onChange={(value) => {
                            const enrichedValue = value.map((e) => {
                              const user = users.find((u) => u._id === e);
                              return {
                                userId: e,
                                ...(user || {}),
                              };
                            });
                            const slackUsersNotInvited = enrichedValue.filter(
                              (e) => e.slackUserId && !e.role
                            );
                            const funcToCall =
                              slackUsersNotInvited &&
                              slackUsersNotInvited.length > 0
                                ? bulkAddUsers({
                                    users: slackUsersNotInvited,
                                    sendInvitationEmail: false,
                                  })
                                : Promise.resolve();
                            funcToCall.then((res) => {
                              const enrichedValueFiltered =
                                enrichedValue.filter(
                                  (e) =>
                                    !slackUsersNotInvited.find(
                                      (e2) => e2.slackUserId === e.slackUserId
                                    )
                                );
                              const reportsAggregated = [
                                ...enrichedValueFiltered,
                                ...(res?.data ? res?.data : []),
                              ].map((e) => ({
                                userId: e._id,
                              }));
                              updateParticipantDetails(record._id, {
                                reports: reportsAggregated,
                              });
                            });
                          }}
                          filterOption={(val, opt) => {
                            const name =
                              `${opt.label.props.user.profile.firstName}` +
                              `${opt.label.props.user.profile.lastName}`;
                            return name
                              .toLowerCase()
                              .includes(val.toLowerCase());
                          }}
                          options={users.map((u) => ({
                            label: <UserDisplayInSelect user={u} />,
                            value: u._id,
                          }))}
                        />
                      );
                    },
                  },
                ]
              : []),
            {
              title: '',
              dataIndex: 'actions',
              key: 'actions',
              width: 100,
              render: (_, record) => (
                <Button
                  type="link"
                  danger
                  disabled={cycle?.isArchived}
                  onClick={() => {
                    Modal.confirm({
                      title: 'Remove Participant',
                      content: (
                        <div>
                          Are you sure you want to remove this participant?
                          {cycle.status === CYCLE_STATUS.LIVE && (
                            <div style={{ marginTop: 12 }}>
                              <Alert
                                showIcon={true}
                                description="The cycle is live. This will delete any content
                              the user wrote. This action cannot be undone."
                                type="error"
                              />
                            </div>
                          )}
                        </div>
                      ),
                      okText: 'Yes, remove',
                      okType: 'danger',
                      cancelText: 'No, keep it',
                      onOk: () => {
                        removeParticipants(record._id);
                      },
                    });
                  }}
                >
                  Remove
                </Button>
              ),
            },
          ]}
          dataSource={cycle?.participants}
        />
      </div>
      
      {modalSelectParticipantsOpen && (
        <SelectParticipantsModal
          participantsAlreadyInCycle={cycle?.participants.map((p) => p._id)}
          open={modalSelectParticipantsOpen}
          onCancel={() => setModalSelectParticipantsOpen(false)}
          onFinish={addParticipants}
          cycleId={cycle._id}
        />
      )}
    </div>
  );
};

export default CycleParticipants;
